import { playSample } from './audio'
import { TIMER_TICK_SECONDS } from './constants'
import { FAIL_GAMEOVER_SFX, GOAL_SUCCESS_SFX } from './missionSfx'
import { snapshotAndConceal } from './primitives/serverStorageTemplate'
import {
  findStateByName,
  showHudText,
  spawnProgrammedClone,
} from './sharedHelpers'
import {
  BehaviorContext,
  BehaviorHandler,
  HudStyle,
  MissionBehaviors,
} from './types'

/**
 * RJS03（30 びょう おちずに いきのこれ！）のミッション環境ビヘイビア。
 *
 * テンプレート rbxl（src/missions/templates/RJS03.rbxl）のギミックを移植する:
 * - ServerScriptService/CreateScript: プレイ開始時に ServerStorage の
 *   ①ブロック を 30 個クローンし、すべて (0,10,0) へ置いて workspace に
 *   親付けする（Clone() で複製された学習者プログラムが各クローンで走る）。
 * - StartPart/StartScript: 透明な開始判定（y=30 の 120×120）にキャラクターが
 *   触れたら（1 回だけ）タイマーを開始し、0.5 秒後にスタート台
 *   （②スタートちてん）を CanCollide=false + 透明化して足場を消す。
 * - Baseplate/BaseScript: 地面にキャラクターが触れたら（1 回だけ）判定。
 *   タイマーが 30 以上なら「クリア！おめでとう！」+ Crack The Safe、
 *   未満なら「ざんねん・・・」+ Loser。
 * - StarterGui/LocalScript: タイマー表示（開始から毎秒 +1。touched で停止）と
 *   開始指示「30びょう おちずに いきのこれ！」・結果表示の切り替え。
 *
 * プレビューでの近似・意図的な差異:
 * - 実機はタイマー（左上の小さなバッジ、初期表示 0）と指示バナーが同時に
 *   表示される。プレビューも HUD を 2 スロットに分け、カウントは 'count'
 *   スロット（左上バッジ）に常時、指示・結果は 'message' スロット（バナー）に
 *   表示して同時表示を再現する。カウントは開始前の 0 から表示し、地面接触
 *   （結果表示）後もそのまま残す（クリア・ざんねんと同時にカウントが見える）。
 * - 効果音はクリア（Crack The Safe）に成功音、失敗（Loser）に失敗音を実音源で
 *   鳴らす。
 * - BGM はシーン JSON の bgm 機構の担当（ビヘイビア対象外）。実機の
 *   'Action Theme (a)' はプロダクト指定の Sunrise（bgm-overrides.json）へ
 *   差し替え済み。
 */

/** ブロックのテンプレート名（実機では ServerStorage に置かれている）。 */
const BLOCK_TEMPLATE_NAME = '①ブロック'

/** クローンするブロック数（Lua: CreateScript の total = 30）。 */
const BLOCK_CLONE_COUNT = 30

/** クローンの配置位置（Lua: clone.Position = Vector3.new(0,10,0)）。 */
const BLOCK_SPAWN_POSITION = { x: 0, y: 10, z: 0 } as const

/** 開始判定・地面・スタート台のオブジェクト名。 */
const START_PART_NAME = 'StartPart'
const BASEPLATE_NAME = 'Baseplate'
const SPAWN_PAD_NAME = '②スタートちてん'

/** スタート台を消すまでの秒数（Lua: StartScript の wait(0.5)）。 */
const SPAWN_PAD_HIDE_DELAY_SECONDS = 0.5

/** クリアに必要な経過秒数（Lua: timer >= 30）。 */
const CLEAR_THRESHOLD_SECONDS = 30

/** カウント（タイマー）の HUD スロット。指示・結果と同時表示するため分離する。 */
const COUNT_SLOT = 'count'

/** 指示・結果バナーの HUD スロット（カウントと別枠で同時表示する）。 */
const MESSAGE_SLOT = 'message'

/** 開始指示の文言（StarterGui/StartTextLabel の実測値）。 */
const START_TEXT = '30びょう　おちずに　いきのこれ！'

/** 開始指示 GUI の実機デザイン（ScreenGui/StartTextLabel の実測値）。 */
const START_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#000000',
  border: '#00007F',
  borderWidth: 5,
  wide: true,
  top: 100,
}

/** タイマー GUI の実機デザイン（ScreenGui/TextLabel の実測値）。 */
const TIMER_HUD_STYLE: HudStyle = {
  background: '#FFFF7F',
  color: '#000000',
  border: '#FF5500',
  borderWidth: 5,
  left: 10,
  top: 10,
}

/** クリア GUI の実機デザイン（ScreenGui/ClearTextLabel の実測値）。 */
const CLEAR_HUD_STYLE: HudStyle = {
  background: '#AAFFFF',
  color: '#000000',
  border: '#00007F',
  borderWidth: 10,
  wide: true,
  top: 100,
}

/** 失敗 GUI の実機デザイン（ScreenGui/NotClearTextLabel の実測値）。 */
const NOT_CLEAR_HUD_STYLE: HudStyle = {
  background: '#FFFFFF',
  color: '#550000',
  border: '#550000',
  borderWidth: 10,
  wide: true,
  top: 100,
}

export const createRjs03Behaviors = (): MissionBehaviors => {
  // Lua: StarterGui/LocalScript の timer / touched と StartScript の touched。
  // プレイ毎に factory が作り直すことでリセットされる。
  let timerSeconds = 0
  let isStartTouched = false
  let isBaseTouched = false

  /**
   * ブロックの一括生成（Lua: CreateScript）。テンプレート本体は ServerStorage
   * 相当としてプレイ中隠し、クローン 30 個を (0,10,0) へ置く。Roblox の
   * Clone() は配下の Script ごと複製するため、学習者が ①ブロック に付けた
   * プログラムを各クローンでも起動する。
   */
  const setupBlocks: BehaviorHandler = (ctx) => {
    const { world } = ctx
    // 複製元はこの時点のテンプレート状態（実機の ServerStorage 保存状態に相当）。
    const snapshot = snapshotAndConceal(world, [BLOCK_TEMPLATE_NAME]).get(
      BLOCK_TEMPLATE_NAME,
    )
    if (!snapshot) {
      return
    }
    for (let index = 0; index < BLOCK_CLONE_COUNT; index += 1) {
      // Lua は Parent 前に Position を代入する（出現時点で配置済み）。
      spawnProgrammedClone(ctx, snapshot, 'rjs03_block', BLOCK_SPAWN_POSITION)
    }
  }

  /** 開始指示の表示（Lua: StartTextLabel は参加時から Visible=true）。 */
  const showStartMessage: BehaviorHandler = showHudText(
    START_TEXT,
    START_HUD_STYLE,
    MESSAGE_SLOT,
  )

  /**
   * カウントの初期表示（Lua: タイマー TextLabel の初期 Text=0）。スタート前
   * から 0 を表示し、指示バナーと同時に見えるようにする。
   */
  const showInitialCount: BehaviorHandler = showHudText(
    String(timerSeconds),
    TIMER_HUD_STYLE,
    COUNT_SLOT,
  )

  /**
   * タイマーループ（Lua: LocalScript の start()）。地面接触（touched）まで
   * 毎秒 +1 して表示する。Lua と同じく「+1 → 表示 → wait(1)」の順。
   */
  const runTimer = async (ctx: BehaviorContext): Promise<void> => {
    while (!isBaseTouched && !ctx.signal.aborted) {
      timerSeconds += 1
      ctx.setHudText?.(String(timerSeconds), TIMER_HUD_STYLE, COUNT_SLOT)
      await ctx.wait(TIMER_TICK_SECONDS)
    }
  }

  /**
   * 開始判定の接触（Lua: StartScript + LocalScript の start()）。
   * 1 回だけタイマーを開始し、0.5 秒後にスタート台を消す。地面接触済み
   * （結果表示中）ならタイマーは 1 周も回らない（Lua の while 条件と同じ）。
   */
  const onStartPartTouched: BehaviorHandler = async (ctx) => {
    if (isStartTouched) {
      return
    }
    isStartTouched = true
    const timerRun = runTimer(ctx)
    // Lua: StartEvent 発火 → wait(0.5) → スタート台のすり抜け + 透明化。
    await ctx.wait(SPAWN_PAD_HIDE_DELAY_SECONDS)
    if (!ctx.signal.aborted) {
      const spawnPad = findStateByName(ctx.world, SPAWN_PAD_NAME)
      if (spawnPad) {
        ctx.world.setCanCollide(spawnPad.id, false)
        ctx.world.setTransparency(spawnPad.id, 1)
      }
    }
    await timerRun
  }

  /**
   * 地面の接触判定（Lua: BaseScript + LocalScript の touch()）。1 回だけ、
   * その時点のタイマーで 30 秒生き残れたかを判定して結果を表示する。
   */
  const onBaseplateTouched: BehaviorHandler = (ctx) => {
    if (isBaseTouched) {
      return
    }
    isBaseTouched = true
    if (timerSeconds >= CLEAR_THRESHOLD_SECONDS) {
      playSample(GOAL_SUCCESS_SFX.url, GOAL_SUCCESS_SFX.volume, ctx.signal)
      ctx.setHudText?.('クリア！おめでとう！', CLEAR_HUD_STYLE, MESSAGE_SLOT)
    } else {
      playSample(FAIL_GAMEOVER_SFX.url, FAIL_GAMEOVER_SFX.volume, ctx.signal)
      ctx.setHudText?.('ざんねん・・・', NOT_CLEAR_HUD_STYLE, MESSAGE_SLOT)
    }
  }

  const onTouchCharacter = new Map<string, BehaviorHandler>()
  onTouchCharacter.set(START_PART_NAME, onStartPartTouched)
  onTouchCharacter.set(BASEPLATE_NAME, onBaseplateTouched)

  return {
    onPlay: [setupBlocks, showStartMessage, showInitialCount],
    onTouchCharacter,
    // ①ブロック は実機では ServerStorage（Script 非実行）。本体では学習者
    // プログラムを起動せず、クローンにだけ startObjectPrograms で起動する。
    serverStorageTemplates: [BLOCK_TEMPLATE_NAME],
  }
}
